import { useState, useEffect, useRef } from 'react'
import React from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../context/auth'
import { supabase } from '../lib/supabase'
import { logActivity, logPageView } from '../lib/activityLogger'
import { ArrowLeft, Search, Package, ShoppingCart, Plus, Minus, Filter, Star, CheckCircle, Car, Cog, Zap, Droplet, Wrench, Clock, Truck, Heart, Share2, X, ChevronDown, ChevronRight, MapPin, Shield, Award, Zap as Lightning, TrendingUp, Tag, Trash2, LogOut } from 'lucide-react'





export default function PartsMarketplacePage() {
  console.log('=== PartsMarketplacePage render ===')
  const navigate = useNavigate()
  const { signOut, session, profile } = useAuth()
  console.log('Session:', session ? session.user.id : 'null')
  console.log('Profile:', profile ? profile.id : 'null')
  const [selectedCategory, setSelectedCategory] = useState('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [carMakeFilter, setCarMakeFilter] = useState('')
  const [carModelFilter, setCarModelFilter] = useState('')
  const [parts, setParts] = useState<any[]>([])
  const [categories, setCategories] = useState<{id: string, name: string}[]>([])
  const [loading, setLoading] = useState(true)
  const [cart, setCart] = useState<Record<string, number>>({})
  const [wishlist, setWishlist] = useState<Set<string>>(new Set())
  const [wishlistItems, setWishlistItems] = useState<any[]>([])
  const [showWishlistModal, setShowWishlistModal] = useState(false)
  const [selectedPart, setSelectedPart] = useState<any | null>(null)
  const [showQuickView, setShowQuickView] = useState(false)
  const [sortBy, setSortBy] = useState('relevance')
  const [priceRange, setPriceRange] = useState({ min: 0, max: null })
  const [showFilters, setShowFilters] = useState(false)
  const [showCart, setShowCart] = useState(false)
  const [showCheckout, setShowCheckout] = useState(false)
  const [checkoutStep, setCheckoutStep] = useState(1)
  const [locationSearch, setLocationSearch] = useState('')
  const [locationSuggestions, setLocationSuggestions] = useState<any[]>([])
  const [selectedLocation, setSelectedLocation] = useState('')
  const [isSearchingLocation, setIsSearchingLocation] = useState(false)
  const locationInputRef = React.useRef<HTMLInputElement>(null)
  const [isSelectingLocation, setIsSelectingLocation] = useState(false)
  const [showOrderSuccess, setShowOrderSuccess] = useState(false)
  const [orderReference, setOrderReference] = useState('')
  const [vendorCount, setVendorCount] = useState(0)

  // Fetch categories from Supabase
  useEffect(() => {
    const loadCategories = async () => {
      if (!session) return
      try {
        const { data, error } = await supabase
          .from('part_categories')
          .select('id, name')
          .eq('is_active', true)
          .order('name')

        if (error) {
          console.error('Error loading categories:', error)
        } else {
          setCategories(data || [])
        }
      } catch (error) {
        console.error('Error loading categories:', error)
      }
    }

    loadCategories()
  }, [session])

  // Fetch parts from Supabase
  useEffect(() => {
    console.log('=== fetchParts useEffect ===', 'session:', session?.user?.id)
    const fetchParts = async () => {
      if (!session) {
        console.log('No session, skipping parts fetch')
        setLoading(false)
        return
      }
      console.log('Starting parts fetch...')
      setLoading(true)
      try {
        // Simple query without join first
        console.log('Fetching parts without join...')
        const { data, error } = await supabase
          .from('parts')
          .select('*')
          .eq('is_active', true)
          .gt('stock', 0)
          .order('created_at', { ascending: false })

        console.log('Parts query result:', data, error)
        console.log('Number of parts fetched:', data?.length)

        if (error) {
          console.error('Error fetching parts:', error)
          setParts([])
          setVendorCount(0)
        } else {
          // Add placeholder vendor info
          const partsWithVendor = (data || []).map((part: any) => ({
            ...part,
            vendor: 'Shop',
            vendorId: part.vendor_id,
            vendorPhone: null,
            vendorAddress: null
          }))
          console.log('Parts with placeholder vendor:', partsWithVendor)
          setParts(partsWithVendor)

          // Count unique vendors
          const uniqueVendors = new Set((data || []).map((p: any) => p.vendor_id))
          setVendorCount(uniqueVendors.size)
        }
      } catch (error) {
        console.error('Error fetching parts:', error)
        setParts([])
        setVendorCount(0)
      } finally {
        setLoading(false)
      }
    }

    fetchParts()
  }, [session])

  // Load wishlist from database on mount
  useEffect(() => {
    const loadWishlist = async () => {
      if (!session) return
      const { data } = await supabase
        .from('wishlist')
        .select('*')
        .eq('user_id', session.user.id)
      if (data) {
        const partIds = new Set(data.map((item: any) => item.part_id))
        setWishlist(partIds)
        setWishlistItems(data)
      }
    }
    loadWishlist()
  }, [session])

  // Log page view on mount
  useEffect(() => {
    logPageView('/parts-marketplace')
  }, [])

  const filteredParts = parts.filter(part => {
    const matchesCategory = selectedCategory === 'all' || part.category === selectedCategory
    const matchesSearch = !searchQuery || (
      part.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (part.description && part.description.toLowerCase().includes(searchQuery.toLowerCase())) ||
      part.vendor.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (part.brand && part.brand.toLowerCase().includes(searchQuery.toLowerCase()))
    )
    const matchesCarMake = !carMakeFilter ||
                         (part.car_make && part.car_make.toLowerCase().includes(carMakeFilter.toLowerCase()))
    const matchesCarModel = !carModelFilter ||
                          (part.car_model && part.car_model.toLowerCase().includes(carModelFilter.toLowerCase()))
    const matchesPrice = part.price >= priceRange.min && (priceRange.max === null || part.price <= priceRange.max)

    return matchesCategory && matchesSearch && matchesCarMake && matchesCarModel && matchesPrice
  }).sort((a, b) => {
    switch (sortBy) {
      case 'price_low':
        return a.price - b.price
      case 'price_high':
        return b.price - a.price
      case 'newest':
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      case 'stock':
        return b.stock - a.stock
      default:
        return 0
    }
  })

  const addToCart = (partId: string) => {
    console.log('Add to cart called for part:', partId)
    const part = parts.find(p => p.id === partId)
    if (!part) {
      console.log('Part not found:', partId)
      return
    }
    const currentQty = cart[partId] || 0
    console.log('Current qty:', currentQty, 'Stock:', part.stock)
    if (currentQty < part.stock) {
      setCart(prev => ({ ...prev, [partId]: currentQty + 1 }))
      console.log('Added to cart, new qty:', currentQty + 1)
    } else {
      console.log('Cannot add - stock limit reached')
    }
  }

  const removeFromCart = (partId: string) => {
    console.log('Remove from cart called for part:', partId)
    setCart(prev => {
      const newCart = { ...prev }
      if (newCart[partId] > 1) {
        newCart[partId]--
        console.log('Decremented qty to:', newCart[partId])
      } else {
        delete newCart[partId]
        console.log('Removed from cart')
      }
      return newCart
    })
  }

  const cartItems = Object.entries(cart).map(([id, qty]) => ({
    part: parts.find(p => p.id === id),
    quantity: qty
  })).filter(item => item.part)

  const cartTotal = cartItems.reduce((sum, item) => sum + (item.part!.price * item.quantity), 0)

  const toggleWishlist = async (partId: string) => {
    console.log('Toggle wishlist called for part:', partId)
    if (!session) {
      alert('Please sign in to use wishlist')
      return
    }

    const part = parts.find(p => p.id === partId)
    if (!part) {
      console.log('Part not found:', partId)
      return
    }

    console.log('Part found:', part.name, 'In wishlist:', wishlist.has(partId))

    if (wishlist.has(partId)) {
      // Remove from wishlist
      console.log('Removing from wishlist...')
      const { error } = await supabase
        .from('wishlist')
        .delete()
        .eq('user_id', session.user.id)
        .eq('part_id', partId)

      if (error) {
        console.error('Error removing from wishlist:', error)
        alert('Failed to remove from wishlist')
        return
      }

      setWishlist(prev => {
        const newWishlist = new Set(prev)
        newWishlist.delete(partId)
        return newWishlist
      })

      setWishlistItems(prev => prev.filter(item => item.part_id !== partId))

      // Log wishlist update
      logActivity('wishlist_updated', '/parts-marketplace', { part_id: partId, part_name: part.name, action: 'removed' })
      console.log('Removed from wishlist successfully')
    } else {
      // Add to wishlist
      console.log('Adding to wishlist...')
      const { error } = await supabase
        .from('wishlist')
        .insert({
          user_id: session.user.id,
          part_id: partId,
          part_name: part.name,
          part_price: part.price,
          vendor_id: part.vendor_id
        })

      if (error) {
        console.error('Error adding to wishlist:', error)
        alert('Failed to add to wishlist')
        return
      }

      setWishlist(prev => {
        const newWishlist = new Set(prev)
        newWishlist.add(partId)
        return newWishlist
      })

      setWishlistItems(prev => [
        ...prev,
        {
          id: crypto.randomUUID(),
          user_id: session.user.id,
          part_id: partId,
          part_name: part.name,
          part_price: part.price,
          vendor_id: part.vendor_id,
          created_at: new Date().toISOString()
        }
      ])

      // Log wishlist update
      logActivity('wishlist_updated', '/parts-marketplace', { part_id: partId, part_name: part.name, action: 'added' })
      console.log('Added to wishlist successfully')
    }
  }

  const openQuickView = (part: any) => {
    setSelectedPart(part)
    setShowQuickView(true)
  }

  const searchLocation = async (query: string) => {
    if (query.length < 2) {
      setLocationSuggestions([])
      return
    }
    setIsSearchingLocation(true)
    try {
      // Search without country code filter to get more results
      const response = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=8&addressdetails=1`)
      const data = await response.json()
      setLocationSuggestions(data)
    } catch (error) {
      console.error('Location search error:', error)
      setLocationSuggestions([])
    } finally {
      setIsSearchingLocation(false)
    }
  }

  // Debounced location search
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (locationSearch) {
        searchLocation(locationSearch)
      }
    }, 300)
    return () => clearTimeout(timeoutId)
  }, [locationSearch])

  if (!session) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg)' }}>
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <Package size={64} style={{ margin: '0 auto 1rem', opacity: 0.5 }} />
          <h2 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '1rem' }}>Sign In Required</h2>
          <p style={{ color: 'var(--muted)', marginBottom: '1.5rem' }}>Please sign in to browse the parts marketplace</p>
          <button
            onClick={() => navigate('/')}
            style={{
              padding: '0.75rem 1.5rem',
              background: 'var(--red)',
              color: 'white',
              border: 'none',
              borderRadius: 'var(--radius-sm)',
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: '0.9rem'
            }}
          >
            Go to Sign In
          </button>
        </div>
      </div>
    )
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)' }}>
      {/* Header */}
      <header style={{
        background: 'white',
        padding: '1rem 2rem',
        borderBottom: '1px solid var(--border)',
        position: 'sticky',
        top: 0,
        zIndex: 100,
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
      }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flex: 1, minWidth: 0 }}>
            <button
              onClick={() => navigate('/')}
              style={{
                padding: '0.5rem 0.75rem',
                background: '#fef2f2',
                border: '1px solid #dc2626',
                borderRadius: '0.375rem',
                cursor: 'pointer',
                color: '#dc2626',
                fontWeight: 600,
                fontSize: '0.8rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                transition: 'all 0.2s',
                flexShrink: 0
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#fee2e2'}
              onMouseLeave={(e) => e.currentTarget.style.background = '#fef2f2'}
            >
              <ArrowLeft size={14} />
              <span className="desktop-only">Back</span>
            </button>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', minWidth: 0 }}>
              <span style={{ fontSize: '1.25rem', fontWeight: 800, color: 'var(--red)', flexShrink: 0 }}>Mk</span>
              <span style={{ fontSize: '1.25rem', fontWeight: 800, color: 'var(--text)', flexShrink: 0 }}>ononi</span>
              <span className="desktop-only" style={{ fontSize: '0.7rem', color: 'var(--muted)', fontWeight: 500, marginLeft: '0.25rem' }}>Parts Marketplace</span>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexShrink: 0 }}>
            <button
              onClick={signOut}
              className="signout-button"
              style={{
                padding: '0.5rem 0.75rem',
                background: 'white',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-sm)',
                cursor: 'pointer',
                fontWeight: 600,
                color: 'var(--text)',
                display: 'flex',
                alignItems: 'center',
                gap: '0.375rem',
                fontSize: '0.8rem'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = 'var(--bg)'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
            >
              <LogOut size={14} />
              <span className="desktop-only">Sign Out</span>
            </button>
            <button
              onClick={() => setShowWishlistModal(true)}
              style={{
                padding: '0.5rem 0.75rem',
                background: 'white',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-sm)',
                cursor: 'pointer',
                fontWeight: 600,
                color: 'var(--text)',
                display: 'flex',
                alignItems: 'center',
                gap: '0.375rem',
                fontSize: '0.8rem'
              }}
            >
              <Heart size={14} />
              <span className="desktop-only">Wishlist ({wishlist.size})</span>
              <span className="mobile-only">{wishlist.size}</span>
            </button>
            <button
              onClick={() => setShowCart(true)}
              style={{
                padding: '0.5rem 0.75rem',
                background: 'var(--red)',
                border: 'none',
                borderRadius: 'var(--radius-sm)',
                cursor: 'pointer',
                fontWeight: 600,
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                gap: '0.375rem',
                fontSize: '0.8rem',
                boxShadow: '0 2px 8px rgba(192, 57, 43, 0.3)',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-1px)'}
              onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
            >
              <ShoppingCart size={16} />
              <span className="desktop-only">Cart ({cartItems.length})</span>
              <span className="mobile-only">{cartItems.length}</span>
              {cartTotal > 0 && <span className="desktop-only" style={{ background: 'rgba(255,255,255,0.2)', padding: '0.2rem 0.4rem', borderRadius: '4px', fontSize: '0.7rem' }}>KES {cartTotal.toLocaleString()}</span>}
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div style={{ width: '100%', margin: '0 auto', padding: '1.5rem' }}>
        {/* Hero Banner */}
        <div style={{
          background: 'linear-gradient(135deg, #C0392B 0%, #E74C3C 100%)',
          borderRadius: 'var(--radius-lg)',
          padding: '1.5rem',
          marginBottom: '1.5rem',
          color: 'white',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <div style={{ position: 'relative', zIndex: 1 }}>
            <h1 style={{ fontSize: '1.5rem', fontWeight: 800, marginBottom: '0.5rem', lineHeight: 1.3 }}>Find Quality Auto Parts</h1>
            <p style={{ fontSize: '0.95rem', opacity: 0.9, marginBottom: '1rem', maxWidth: '600px', lineHeight: 1.5 }}>
              Browse genuine and aftermarket parts from verified vendors across Kenya. Fast delivery, guaranteed quality.
            </p>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', background: 'rgba(255,255,255,0.2)', padding: '0.5rem 0.875rem', borderRadius: '20px', fontSize: '0.8rem', fontWeight: 600 }}>
                <Shield size={14} />
                <span>Verified Vendors</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', background: 'rgba(255,255,255,0.2)', padding: '0.5rem 0.875rem', borderRadius: '20px', fontSize: '0.8rem', fontWeight: 600 }}>
                <Truck size={14} />
                <span>Fast Delivery</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', background: 'rgba(255,255,255,0.2)', padding: '0.5rem 0.875rem', borderRadius: '20px', fontSize: '0.8rem', fontWeight: 600 }}>
                <Award size={14} />
                <span>Quality Guaranteed</span>
              </div>
            </div>
          </div>
        </div>

        {/* Search and Filter */}
        <div style={{
          background: 'white',
          padding: '1rem',
          borderRadius: 'var(--radius)',
          border: '1px solid var(--border)',
          marginBottom: '1.5rem',
          boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
        }}>
          <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: '200px', position: 'relative' }}>
              <Search
                size={18}
                style={{
                  position: 'absolute',
                  left: '0.75rem',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'var(--muted)'
                }}
              />
              <input
                type="text"
                placeholder="Search parts, brands, vendors..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.75rem 1rem 0.75rem 2.5rem',
                  border: '2px solid var(--border)',
                  borderRadius: 'var(--radius-sm)',
                  fontSize: '0.95rem',
                  outline: 'none',
                  transition: 'border-color 0.2s'
                }}
                onFocus={(e) => e.currentTarget.style.borderColor = 'var(--red)'}
                onBlur={(e) => e.currentTarget.style.borderColor = 'var(--border)'}
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  style={{
                    position: 'absolute',
                    right: '0.75rem',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: 'var(--muted)',
                    padding: '0.25rem'
                  }}
                >
                  <X size={14} />
                </button>
              )}
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              style={{
                padding: '0.75rem 1rem',
                background: showFilters ? 'var(--red)' : 'white',
                border: showFilters ? '1px solid var(--red)' : '1px solid var(--border)',
                borderRadius: 'var(--radius-sm)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                fontWeight: 600,
                color: showFilters ? 'white' : 'var(--text)',
                transition: 'all 0.2s',
                fontSize: '0.9rem',
                flexShrink: 0
              }}
            >
              <Filter size={16} />
              Filters
              {showFilters && <X size={14} />}
            </button>
          </div>

          {/* Expandable Filters */}
          {showFilters && (
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
              gap: '1rem', 
              paddingTop: '1rem', 
              borderTop: '1px solid var(--border)' 
            }}>
              <div>
                <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem', fontSize: '0.85rem', color: 'var(--muted)' }}>Car Make</label>
                <input
                  type="text"
                  placeholder="e.g., Toyota, Nissan"
                  value={carMakeFilter}
                  onChange={(e) => setCarMakeFilter(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.6rem 0.75rem',
                    border: '2px solid var(--border)',
                    borderRadius: 'var(--radius-sm)',
                    fontSize: '0.875rem'
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem', fontSize: '0.85rem', color: 'var(--muted)' }}>Car Model</label>
                <input
                  type="text"
                  placeholder="e.g., Corolla, Camry"
                  value={carModelFilter}
                  onChange={(e) => setCarModelFilter(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.6rem 0.75rem',
                    border: '2px solid var(--border)',
                    borderRadius: 'var(--radius-sm)',
                    fontSize: '0.875rem'
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem', fontSize: '0.85rem', color: 'var(--muted)' }}>Price Range</label>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  <input
                    type="number"
                    placeholder="Min"
                    value={priceRange.min}
                    onChange={(e) => setPriceRange({ ...priceRange, min: Number(e.target.value) })}
                    style={{
                      flex: 1,
                      padding: '0.6rem 0.75rem',
                      border: '2px solid var(--border)',
                      borderRadius: 'var(--radius-sm)',
                      fontSize: '0.875rem'
                    }}
                  />
                  <span>-</span>
                  <input
                    type="number"
                    placeholder="Max (optional)"
                    value={priceRange.max || ''}
                    onChange={(e) => setPriceRange({ ...priceRange, max: e.target.value ? Number(e.target.value) : null as number | null })}
                    style={{
                      flex: 1,
                      padding: '0.6rem 0.75rem',
                      border: '2px solid var(--border)',
                      borderRadius: 'var(--radius-sm)',
                      fontSize: '0.875rem'
                    }}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Categories */}
          <div style={{ display: 'flex', gap: '0.375rem', flexWrap: 'wrap', overflowX: 'auto', paddingBottom: '0.5rem', WebkitOverflowScrolling: 'touch' }}>
            <button
              key="all"
              onClick={() => setSelectedCategory('all')}
              style={{
                padding: '0.5rem 0.875rem',
                background: selectedCategory === 'all' ? 'var(--red)' : 'white',
                border: selectedCategory === 'all' ? '1px solid var(--red)' : '1px solid var(--border)',
                borderRadius: 'var(--radius-sm)',
                cursor: 'pointer',
                fontWeight: 600,
                color: selectedCategory === 'all' ? 'white' : 'var(--text)',
                display: 'flex',
                alignItems: 'center',
                gap: '0.375rem',
                fontSize: '0.8rem',
                transition: 'all 0.2s',
                boxShadow: selectedCategory === 'all' ? '0 2px 8px rgba(192, 57, 43, 0.3)' : 'none',
                flexShrink: 0,
                whiteSpace: 'nowrap'
              }}
              onMouseEnter={(e) => {
                if (selectedCategory !== 'all') {
                  e.currentTarget.style.borderColor = 'var(--red)'
                  e.currentTarget.style.color = 'var(--red)'
                }
              }}
              onMouseLeave={(e) => {
                if (selectedCategory !== 'all') {
                  e.currentTarget.style.borderColor = 'var(--border)'
                  e.currentTarget.style.color = 'var(--text)'
                }
              }}
            >
              <Package size={14} />
              All Parts
            </button>
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.name)}
                style={{
                  padding: '0.5rem 0.875rem',
                  background: selectedCategory === category.name ? 'var(--red)' : 'white',
                  border: selectedCategory === category.name ? '1px solid var(--red)' : '1px solid var(--border)',
                  borderRadius: 'var(--radius-sm)',
                  cursor: 'pointer',
                  fontWeight: 600,
                  color: selectedCategory === category.name ? 'white' : 'var(--text)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.375rem',
                  fontSize: '0.8rem',
                  transition: 'all 0.2s',
                  boxShadow: selectedCategory === category.name ? '0 2px 8px rgba(192, 57, 43, 0.3)' : 'none',
                  flexShrink: 0,
                  whiteSpace: 'nowrap'
                }}
                onMouseEnter={(e) => {
                  if (selectedCategory !== category.name) {
                    e.currentTarget.style.borderColor = 'var(--red)'
                    e.currentTarget.style.color = 'var(--red)'
                  }
                }}
                onMouseLeave={(e) => {
                  if (selectedCategory !== category.name) {
                    e.currentTarget.style.borderColor = 'var(--border)'
                    e.currentTarget.style.color = 'var(--text)'
                  }
                }}
              >
                {category.name}
              </button>
            ))}
          </div>
        </div>

        {/* Results Info */}
        <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem' }}>
          <div style={{ minWidth: 0 }}>
            <h2 style={{ fontSize: '1.1rem', fontWeight: 700, color: 'var(--text)', marginBottom: '0.25rem' }}>
              {filteredParts.length} parts found
            </h2>
            <p style={{ fontSize: '0.8rem', color: 'var(--muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {selectedCategory !== 'all' && `in ${selectedCategory}`}
              {searchQuery && ` matching "${searchQuery}"`}
            </p>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexShrink: 0 }}>
            <span className="desktop-only" style={{ fontSize: '0.8rem', color: 'var(--muted)', fontWeight: 500 }}>Sort by:</span>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              style={{
                padding: '0.5rem 0.75rem',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-sm)',
                fontSize: '0.8rem',
                fontWeight: 500,
                cursor: 'pointer',
                outline: 'none'
              }}
            >
              <option value="relevance">Relevance</option>
              <option value="price_low">Price: Low to High</option>
              <option value="price_high">Price: High to Low</option>
              <option value="rating">Highest Rated</option>
              <option value="reviews">Most Reviews</option>
              <option value="newest">Newest Arrivals</option>
            </select>
          </div>
        </div>

        {/* Parts Grid */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '4rem 2rem', color: 'var(--muted)' }}>
            <div style={{ 
              width: '48px', 
              height: '48px', 
              border: '4px solid var(--border)', 
              borderTopColor: 'var(--red)', 
              borderRadius: '50%', 
              margin: '0 auto 1rem', 
              animation: 'spin 1s linear infinite' 
            }} />
            <p>Loading parts...</p>
          </div>
        ) : filteredParts.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '4rem 2rem', color: 'var(--muted)' }}>
            <Package size={64} style={{ margin: '0 auto 1rem', opacity: 0.5 }} />
            <p style={{ fontSize: '1rem', fontWeight: 500 }}>No parts found</p>
            <p style={{ fontSize: '0.875rem', marginTop: '0.5rem' }}>Try adjusting your filters or search terms</p>
          </div>
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: '0.75rem',
            width: '100%'
          }}>
            {filteredParts.map((part) => (
              <div
                key={part.id}
                style={{
                  background: 'white',
                  borderRadius: '8px',
                  border: '1px solid var(--border)',
                  overflow: 'hidden',
                  transition: 'all 0.2s ease',
                  cursor: 'pointer',
                  position: 'relative',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                  display: 'flex',
                  flexDirection: 'column'
                }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)'
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.12)'
                e.currentTarget.style.borderColor = 'var(--red)'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)'
                e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.08)'
                e.currentTarget.style.borderColor = 'var(--border)'
              }}
              onClick={() => openQuickView(part)}
            >
              {/* Badges */}
              <div style={{ position: 'absolute', top: '8px', left: '8px', zIndex: 10, display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                {part.isNew && (
                  <span style={{
                    padding: '0.2rem 0.5rem',
                    background: 'linear-gradient(135deg, #10b981, #059669)',
                    color: 'white',
                    borderRadius: '12px',
                    fontSize: '0.6rem',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    boxShadow: '0 1px 4px rgba(16, 185, 129, 0.3)'
                  }}>
                    <Lightning size={8} style={{ display: 'inline', marginRight: '2px', verticalAlign: 'middle' }} />
                    New
                  </span>
                )}
                {part.onSale && (
                  <span style={{
                    padding: '0.2rem 0.5rem',
                    background: 'linear-gradient(135deg, #ef4444, #dc2626)',
                    color: 'white',
                    borderRadius: '12px',
                    fontSize: '0.6rem',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    boxShadow: '0 1px 4px rgba(239, 68, 68, 0.3)'
                  }}>
                    <Tag size={8} style={{ display: 'inline', marginRight: '2px', verticalAlign: 'middle' }} />
                    Sale
                  </span>
                )}
                {part.stock <= 5 && part.stock > 0 && (
                  <span style={{
                    padding: '0.2rem 0.5rem',
                    background: '#fef3c7',
                    color: '#d97706',
                    borderRadius: '12px',
                    fontSize: '0.6rem',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                  }}>
                    Only {part.stock} left
                  </span>
                )}
                {part.stock === 0 && (
                  <span style={{
                    padding: '0.2rem 0.5rem',
                    background: '#fee2e2',
                    color: '#dc2626',
                    borderRadius: '12px',
                    fontSize: '0.6rem',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                  }}>
                    Out of Stock
                  </span>
                )}
              </div>

              {/* Wishlist Button */}
              <button
                onClick={(e) => { e.stopPropagation(); toggleWishlist(part.id) }}
                style={{
                  position: 'absolute',
                  top: '8px',
                  right: '8px',
                  zIndex: 10,
                  background: 'white',
                  border: '1px solid var(--border)',
                  borderRadius: '50%',
                  width: '32px',
                  height: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  boxShadow: '0 1px 4px rgba(0,0,0,0.1)'
                }}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
              >
                <Heart
                  size={14}
                  fill={wishlist.has(part.id) ? '#dc2626' : 'none'}
                  color={wishlist.has(part.id) ? '#dc2626' : 'var(--muted)'}
                />
              </button>

              {/* Part Image */}
              <div style={{
                height: '70px',
                background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '1.25rem',
                borderBottom: '1px solid var(--border)',
                position: 'relative',
                overflow: 'hidden',
                flexShrink: 0
              }}>
                {part.image_url ? (
                  <img 
                    src={part.image_url} 
                    alt={part.name}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                ) : (
                  <span>{part.image || '🔧'}</span>
                )}
                {part.freeShipping && (
                  <div style={{
                    position: 'absolute',
                    bottom: '8px',
                    left: '12px',
                    background: 'rgba(255,255,255,0.95)',
                    padding: '0.375rem 0.75rem',
                    borderRadius: '20px',
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    color: '#059669',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.25rem',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                  }}>
                    <Truck size={12} />
                    Free Shipping
                  </div>
                )}
              </div>

              {/* Part Details */}
              <div style={{ padding: '0.625rem' }}>
                {/* Shop Name */}
                <div style={{ fontSize: '0.65rem', color: 'var(--red)', fontWeight: 600, marginBottom: '0.15rem' }}>
                  {part.vendor}
                </div>

                {/* Name */}
                <h3 style={{
                  fontSize: '0.85rem',
                  fontWeight: 700,
                  color: 'var(--text)',
                  lineHeight: 1.2,
                  marginBottom: '0.25rem',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden'
                }}>
                  {part.name}
                </h3>

                {/* Stock */}
                <div style={{ fontSize: '0.65rem', color: 'var(--muted)', marginBottom: '0.25rem', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                  <Package size={10} />
                  <span>{part.stock} in stock</span>
                </div>

                {/* Vehicle Compatibility */}
                {part.car_make && (
                  <div style={{ fontSize: '0.6rem', color: 'var(--muted)', marginBottom: '0.25rem', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                    <Car size={10} />
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {part.car_make} {part.car_model} {part.year_range && `(${part.year_range})`}
                    </span>
                  </div>
                )}

                {/* Price */}
                <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.25rem', marginBottom: '0.375rem' }}>
                  <span style={{ fontSize: '1rem', fontWeight: 800, color: 'var(--red)' }}>
                    KES {part.price.toLocaleString()}
                  </span>
                </div>

                {/* Add to Cart */}
                {cart[part.id] ? (
                  <div style={{ display: 'flex', gap: '0.25rem' }}>
                    <button
                      onClick={(e) => { e.stopPropagation(); removeFromCart(part.id) }}
                      style={{
                        flex: 1,
                        padding: '0.375rem',
                        background: 'white',
                        border: '1px solid var(--border)',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '0.7rem',
                        fontWeight: 600
                      }}
                    >
                      <Minus size={12} />
                    </button>
                    <div style={{
                      padding: '0.375rem',
                      background: 'var(--red)',
                      color: 'white',
                      borderRadius: '4px',
                      fontSize: '0.75rem',
                      fontWeight: 700,
                      minWidth: '32px',
                      textAlign: 'center'
                    }}>
                      {cart[part.id]}
                    </div>
                    <button
                      onClick={(e) => { e.stopPropagation(); addToCart(part.id) }}
                      style={{
                        flex: 1,
                        padding: '0.375rem',
                        background: 'var(--red)',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        color: 'white'
                      }}
                    >
                      <Plus size={12} />
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={(e) => { e.stopPropagation(); addToCart(part.id) }}
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      background: 'linear-gradient(135deg, var(--red) 0%, #b91c1c 100%)',
                      border: 'none',
                      borderRadius: '4px',
                      color: 'white',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.25rem'
                    }}
                  >
                    <ShoppingCart size={14} />
                    Add to Cart
                  </button>
                )}
              </div>
            </div>
            ))}
          </div>
        )}
      </div>

      {/* Quick View Modal */}
      {showQuickView && selectedPart && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.6)',
            backdropFilter: 'blur(4px)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '1rem',
            animation: 'fadeIn 0.2s ease-out'
          }}
          onClick={() => setShowQuickView(false)}
        >
          <div
            style={{
              background: 'white',
              borderRadius: 'var(--radius-lg)',
              maxWidth: '900px',
              width: '100%',
              maxHeight: '95vh',
              overflow: 'auto',
              position: 'relative',
              animation: 'slideUp 0.3s ease-out'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={() => setShowQuickView(false)}
              style={{
                position: 'absolute',
                top: '1rem',
                right: '1rem',
                background: 'white',
                border: '1px solid var(--border)',
                borderRadius: '50%',
                width: '36px',
                height: '36px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                zIndex: 10,
                transition: 'all 0.2s',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = 'var(--bg)'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
            >
              <X size={18} />
            </button>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0' }}>
              {/* Image Section */}
              <div style={{
                background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
                padding: '2rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '6rem',
                minHeight: '250px'
              }}>
                {selectedPart.image_url ? (
                  <img 
                    src={selectedPart.image_url} 
                    alt={selectedPart.name}
                    style={{ maxWidth: '100%', maxHeight: '250px', objectFit: 'contain' }}
                  />
                ) : (
                  <span>{selectedPart.image || '🔧'}</span>
                )}
              </div>

              {/* Details Section */}
              <div style={{ padding: '2rem' }}>
                {/* Brand */}
                <div style={{ fontSize: '0.8rem', color: 'var(--muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.5rem' }}>
                  {selectedPart.brand}
                </div>

                {/* Name */}
                <h2 style={{ fontSize: '1.5rem', fontWeight: 800, color: 'var(--text)', marginBottom: '0.5rem', lineHeight: 1.3 }}>
                  {selectedPart.name}
                </h2>

                {/* Rating */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        size={14}
                        style={{
                          color: i < Math.floor(selectedPart.rating) ? '#f59e0b' : '#e5e7eb',
                          fill: i < Math.floor(selectedPart.rating) ? '#f59e0b' : 'none'
                        }}
                      />
                    ))}
                    <span style={{ fontSize: '0.9rem', fontWeight: 700, color: 'var(--text)', marginLeft: '0.5rem' }}>
                      {selectedPart.rating}
                    </span>
                  </div>
                  <span style={{ fontSize: '0.85rem', color: 'var(--muted)' }}>
                    ({selectedPart.reviews} reviews)
                  </span>
                </div>

                {/* Price */}
                <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem', marginBottom: '1rem' }}>
                  <span style={{ fontSize: '2rem', fontWeight: 800, color: 'var(--red)' }}>
                    KES {selectedPart.price.toLocaleString()}
                  </span>
                  {selectedPart.onSale && selectedPart.originalPrice > selectedPart.price && (
                    <span style={{ fontSize: '1rem', color: 'var(--muted)', textDecoration: 'line-through' }}>
                      KES {selectedPart.originalPrice.toLocaleString()}
                    </span>
                  )}
                </div>

                {/* Description */}
                <p style={{ fontSize: '0.9rem', color: 'var(--muted)', lineHeight: 1.6, marginBottom: '1rem' }}>
                  {selectedPart.description}
                </p>

                {/* Features */}
                {selectedPart.features && (
                  <div style={{ marginBottom: '1rem' }}>
                    <h4 style={{ fontSize: '0.8rem', fontWeight: 700, color: 'var(--text)', marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Features
                </h4>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.375rem' }}>
                      {selectedPart.features.map((feature: string, index: number) => (
                        <span
                          key={index}
                          style={{
                            padding: '0.3rem 0.6rem',
                            background: 'var(--bg)',
                            border: '1px solid var(--border)',
                            borderRadius: '20px',
                            fontSize: '0.75rem',
                            color: 'var(--text)',
                            fontWeight: 500
                          }}
                        >
                          {feature}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Compatibility */}
                <div style={{ marginBottom: '1rem' }}>
                  <h4 style={{ fontSize: '0.8rem', fontWeight: 700, color: 'var(--text)', marginBottom: '0.375rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Compatible Vehicles
                  </h4>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', color: 'var(--muted)', fontSize: '0.85rem' }}>
                    <Car size={14} style={{ color: 'var(--red)' }} />
                    {selectedPart.compatibility ? selectedPart.compatibility.join(', ') : 'N/A'}
                  </div>
                </div>

                {/* Vendor */}
                <div style={{ marginBottom: '1rem' }}>
                  <h4 style={{ fontSize: '0.8rem', fontWeight: 700, color: 'var(--text)', marginBottom: '0.375rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Sold By
                  </h4>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', color: 'var(--muted)', fontSize: '0.85rem' }}>
                    <Truck size={14} style={{ color: 'var(--red)' }} />
                    {selectedPart.vendor}
                  </div>
                </div>

                {/* Stock Status */}
                <div style={{ marginBottom: '1rem', fontSize: '0.85rem', fontWeight: 600, color: selectedPart.stock > 0 ? 'var(--green)' : 'var(--red)' }}>
                  {selectedPart.stock > 0 ? `${selectedPart.stock} in stock` : 'Out of stock'}
                </div>

                {/* Add to Cart Button */}
                {cart[selectedPart.id] ? (
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button
                      onClick={() => removeFromCart(selectedPart.id)}
                      style={{
                        flex: 1,
                        padding: '0.75rem',
                        background: 'white',
                        border: '2px solid var(--border)',
                        borderRadius: 'var(--radius-sm)',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '0.375rem',
                        fontWeight: 600,
                        fontSize: '0.9rem'
                      }}
                    >
                      <Minus size={16} />
                    </button>
                    <div style={{
                      flex: 1,
                      padding: '0.75rem',
                      background: 'var(--bg)',
                      border: '2px solid var(--border)',
                      borderRadius: 'var(--radius-sm)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 700,
                      fontSize: '1rem'
                    }}>
                      {cart[selectedPart.id]}
                    </div>
                    <button
                      onClick={() => addToCart(selectedPart.id)}
                      disabled={cart[selectedPart.id] >= selectedPart.stock}
                      style={{
                        flex: 1,
                        padding: '0.75rem',
                        background: 'white',
                        border: '2px solid var(--border)',
                        borderRadius: 'var(--radius-sm)',
                        cursor: cart[selectedPart.id] >= selectedPart.stock ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '0.375rem',
                        fontWeight: 600,
                        fontSize: '0.9rem',
                        opacity: cart[selectedPart.id] >= selectedPart.stock ? 0.5 : 1
                      }}
                    >
                      <Plus size={16} />
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => addToCart(selectedPart.id)}
                    disabled={selectedPart.stock === 0}
                    style={{
                      width: '100%',
                      padding: '0.875rem',
                      background: selectedPart.stock > 0 ? 'var(--red)' : 'var(--border)',
                      border: 'none',
                      borderRadius: 'var(--radius-sm)',
                      cursor: selectedPart.stock > 0 ? 'pointer' : 'not-allowed',
                      fontWeight: 700,
                      color: selectedPart.stock > 0 ? 'white' : 'var(--muted)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.5rem',
                      fontSize: '0.95rem',
                      boxShadow: selectedPart.stock > 0 ? '0 4px 12px rgba(192, 57, 43, 0.3)' : 'none'
                    }}
                  >
                    <ShoppingCart size={18} />
                    {selectedPart.stock > 0 ? 'Add to Cart' : 'Out of Stock'}
                  </button>
                )}

                {/* SKU */}
                <div style={{ marginTop: '1rem', fontSize: '0.75rem', color: 'var(--muted)' }}>
                  SKU: {selectedPart.sku || 'N/A'}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Animation Keyframes */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes slideInRight {
          from { opacity: 0; transform: translateX(100%); }
          to { opacity: 1; transform: translateX(0); }
        }
      `}</style>

      {/* Cart Drawer */}
      {showCart && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            backdropFilter: 'blur(4px)',
            zIndex: 1000,
            display: 'flex',
            justifyContent: 'flex-end',
            animation: 'fadeIn 0.2s ease-out'
          }}
          onClick={() => setShowCart(false)}
        >
          <div
            style={{
              background: 'white',
              width: '450px',
              maxWidth: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              animation: 'slideInRight 0.3s ease-out',
              boxShadow: '-4px 0 24px rgba(0,0,0,0.15)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Cart Header */}
            <div style={{
              padding: '1.5rem',
              borderBottom: '1px solid var(--border)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              background: 'white'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <ShoppingCart size={24} style={{ color: 'var(--red)' }} />
                <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--text)' }}>
                  Shopping Cart ({cartItems.length})
                </h2>
              </div>
              <button
                onClick={() => setShowCart(false)}
                style={{
                  background: 'var(--bg)',
                  border: '1px solid var(--border)',
                  borderRadius: '50%',
                  width: '40px',
                  height: '40px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = 'var(--border)'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'var(--bg)'}
              >
                <X size={20} />
              </button>
            </div>

            {/* Cart Items */}
            <div style={{ flex: 1, overflow: 'auto', padding: '1.5rem' }}>
              {cartItems.length === 0 ? (
                <div style={{
                  textAlign: 'center',
                  padding: '3rem 1rem',
                  color: 'var(--muted)'
                }}>
                  <div style={{
                    width: '80px',
                    height: '80px',
                    background: 'var(--bg)',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 1.5rem'
                  }}>
                    <ShoppingCart size={40} style={{ opacity: 0.5 }} />
                  </div>
                  <h3 style={{ fontSize: '1.25rem', fontWeight: 700, marginBottom: '0.5rem', color: 'var(--text)' }}>
                    Your cart is empty
                  </h3>
                  <p style={{ fontSize: '0.95rem', marginBottom: '1.5rem' }}>Add some parts to get started</p>
                  <button
                    onClick={() => setShowCart(false)}
                    style={{
                      padding: '0.75rem 1.5rem',
                      background: 'var(--red)',
                      border: 'none',
                      borderRadius: 'var(--radius-sm)',
                      cursor: 'pointer',
                      fontWeight: 600,
                      color: 'white',
                      fontSize: '0.95rem'
                    }}
                  >
                    Browse Parts
                  </button>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  {cartItems.map((item) => (
                    <div
                      key={item.part!.id}
                      style={{
                        display: 'flex',
                        gap: '1rem',
                        padding: '1rem',
                        background: 'var(--bg)',
                        borderRadius: 'var(--radius-sm)',
                        border: '1px solid var(--border)'
                      }}
                    >
                      {/* Product Image */}
                      <div style={{
                        width: '80px',
                        height: '80px',
                        background: 'white',
                        borderRadius: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '2.5rem',
                        flexShrink: 0
                      }}>
                        {item.part!.image}
                      </div>

                      {/* Product Info */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                          fontSize: '0.95rem',
                          fontWeight: 600,
                          color: 'var(--text)',
                          marginBottom: '0.25rem',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {item.part!.name}
                        </div>
                        <div style={{
                          fontSize: '0.8rem',
                          color: 'var(--muted)',
                          marginBottom: '0.5rem'
                        }}>
                          {item.part!.vendor}
                        </div>
                        <div style={{
                          fontSize: '1rem',
                          fontWeight: 700,
                          color: 'var(--red)',
                          marginBottom: '0.75rem'
                        }}>
                          KES {item.part!.price.toLocaleString()}
                        </div>

                        {/* Quantity Controls */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <button
                            onClick={() => removeFromCart(item.part!.id)}
                            disabled={item.quantity <= 1}
                            style={{
                              padding: '0.5rem 0.75rem',
                              background: 'white',
                              border: '1px solid var(--border)',
                              borderRadius: '6px',
                              cursor: item.quantity > 1 ? 'pointer' : 'not-allowed',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '0.875rem'
                            }}
                          >
                            <Minus size={14} />
                          </button>
                          <span style={{
                            padding: '0.5rem 1rem',
                            background: 'white',
                            border: '1px solid var(--border)',
                            borderRadius: '6px',
                            fontWeight: 600,
                            fontSize: '0.95rem',
                            minWidth: '50px',
                            textAlign: 'center'
                          }}>
                            {item.quantity}
                          </span>
                          <button
                            onClick={() => addToCart(item.part!.id)}
                            disabled={item.quantity >= item.part!.stock}
                            style={{
                              padding: '0.5rem 0.75rem',
                              background: 'white',
                              border: '1px solid var(--border)',
                              borderRadius: '6px',
                              cursor: item.quantity >= item.part!.stock ? 'not-allowed' : 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '0.875rem',
                              opacity: item.quantity >= item.part!.stock ? 0.5 : 1
                            }}
                          >
                            <Plus size={14} />
                          </button>
                          <button
                            onClick={() => removeFromCart(item.part!.id)}
                            style={{
                              padding: '0.5rem 0.75rem',
                              background: '#fee2e2',
                              border: '1px solid #fecaca',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '0.875rem',
                              color: '#dc2626',
                              marginLeft: 'auto'
                            }}
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Cart Footer */}
            {cartItems.length > 0 && (
              <div style={{
                padding: '1.5rem',
                borderTop: '1px solid var(--border)',
                background: 'white'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                  <span style={{ fontSize: '0.95rem', fontWeight: 600, color: 'var(--text)' }}>Subtotal</span>
                  <span style={{ fontSize: '1.25rem', fontWeight: 800, color: 'var(--red)' }}>
                    KES {cartTotal.toLocaleString()}
                  </span>
                </div>
                <div style={{ fontSize: '0.8rem', color: 'var(--muted)', marginBottom: '1rem' }}>
                  Shipping and taxes calculated at checkout
                </div>
                <button
                  onClick={() => { setShowCart(false); setShowCheckout(true) }}
                  style={{
                    width: '100%',
                    padding: '1rem',
                    background: 'var(--red)',
                    border: 'none',
                    borderRadius: 'var(--radius-sm)',
                    cursor: 'pointer',
                    fontWeight: 700,
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.75rem',
                    fontSize: '1rem',
                    boxShadow: '0 4px 12px rgba(192, 57, 43, 0.3)',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)'
                    e.currentTarget.style.boxShadow = '0 6px 16px rgba(192, 57, 43, 0.4)'
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)'
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(192, 57, 43, 0.3)'
                  }}
                >
                  <Package size={20} />
                  Proceed to Checkout
                </button>
                <button
                  onClick={() => setShowCart(false)}
                  style={{
                    width: '100%',
                    padding: '0.875rem',
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    fontWeight: 600,
                    color: 'var(--muted)',
                    fontSize: '0.9rem',
                    marginTop: '0.75rem',
                    transition: 'color 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.color = 'var(--text)'}
                  onMouseLeave={(e) => e.currentTarget.style.color = 'var(--muted)'}
                >
                  Continue Shopping
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Checkout Modal */}
      {showCheckout && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.6)',
            backdropFilter: 'blur(4px)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '2rem',
            animation: 'fadeIn 0.2s ease-out'
          }}
          onClick={() => setShowCheckout(false)}
        >
          <div
            style={{
              background: 'white',
              borderRadius: 'var(--radius-lg)',
              maxWidth: '600px',
              width: '100%',
              maxHeight: '90vh',
              overflow: 'auto',
              position: 'relative',
              animation: 'slideUp 0.3s ease-out'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Checkout Header */}
            <div style={{
              padding: '1.5rem',
              borderBottom: '1px solid var(--border)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              background: 'white',
              zIndex: 10
            }}>
              <h3 style={{ fontSize: '1.5rem', fontWeight: 800, color: 'var(--text)' }}>Checkout</h3>
              <button
                onClick={() => setShowCheckout(false)}
                style={{
                  background: 'var(--bg)',
                  border: '1px solid var(--border)',
                  borderRadius: '50%',
                  width: '40px',
                  height: '40px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = 'var(--border)'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'var(--bg)'}
              >
                <X size={20} />
              </button>
            </div>

            {/* Checkout Content */}
            <div style={{ padding: '2rem' }}>
              {/* Order Summary */}
              <div style={{
                background: 'var(--bg)',
                borderRadius: 'var(--radius-sm)',
                padding: '1.5rem',
                marginBottom: '1.5rem',
                border: '1px solid var(--border)'
              }}>
                <h4 style={{
                  fontSize: '0.875rem',
                  fontWeight: 700,
                  color: 'var(--muted)',
                  marginBottom: '1rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <Package size={16} />
                  Order Summary
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  {cartItems.map((item) => (
                    <div
                      key={item.part!.id}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        paddingBottom: '0.75rem',
                        borderBottom: '1px solid var(--border)'
                      }}
                    >
                      <div style={{ flex: 1 }}>
                        <div style={{
                          fontSize: '0.9rem',
                          fontWeight: 600,
                          color: 'var(--text)',
                          marginBottom: '0.25rem'
                        }}>
                          {item.part!.name}
                        </div>
                        <div style={{ fontSize: '0.8rem', color: 'var(--muted)' }}>
                          Qty: {item.quantity} × KES {item.part!.price.toLocaleString()}
                        </div>
                      </div>
                      <div style={{
                        fontSize: '1rem',
                        fontWeight: 700,
                        color: 'var(--text)'
                      }}>
                        KES {(item.part!.price * item.quantity).toLocaleString()}
                      </div>
                    </div>
                  ))}
                </div>
                <div style={{
                  marginTop: '1rem',
                  paddingTop: '1rem',
                  borderTop: '2px solid var(--border)',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <span style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--text)' }}>Total</span>
                  <span style={{ fontSize: '1.5rem', fontWeight: 800, color: 'var(--red)' }}>
                    KES {cartTotal.toLocaleString()}
                  </span>
                </div>
              </div>

              {/* Delivery Information */}
              <div style={{ marginBottom: '1.5rem' }}>
                <h4 style={{
                  fontSize: '0.875rem',
                  fontWeight: 700,
                  color: 'var(--text)',
                  marginBottom: '1rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}>
                  Delivery Information
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <div className="checkout-location-wrapper" style={{ position: 'relative', zIndex: 50 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                      <label style={{
                        fontSize: '0.875rem',
                        fontWeight: 600,
                        color: 'var(--text)'
                      }}>
                        Delivery Location *
                      </label>
                      {(locationSearch || selectedLocation) && (
                        <button
                          onClick={() => { setLocationSearch(''); setSelectedLocation(''); setLocationSuggestions([]) }}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            color: 'var(--muted)',
                            padding: '0.25rem',
                            display: 'flex',
                            alignItems: 'center'
                          }}
                          title="Clear location"
                        >
                          <X size={16} />
                        </button>
                      )}
                    </div>
                    <input
                      ref={locationInputRef}
                      type="text"
                      placeholder="Search location (e.g., Ruaka Drive 12, Westlands)"
                      value={selectedLocation || locationSearch}
                      onChange={(e) => { setLocationSearch(e.target.value); setSelectedLocation('') }}
                      style={{
                        width: '100%',
                        padding: '0.875rem 1rem',
                        border: '2px solid var(--border)',
                        borderRadius: 'var(--radius-sm)',
                        fontSize: '1rem',
                        outline: 'none',
                        transition: 'border-color 0.2s'
                      }}
                      onFocus={(e) => e.currentTarget.style.borderColor = 'var(--red)'}
                      onBlur={(e) => {
                        e.currentTarget.style.borderColor = 'var(--border)'
                        // Only clear suggestions if not selecting a location
                        if (!isSelectingLocation) {
                          setTimeout(() => setLocationSuggestions([]), 200)
                        }
                        setIsSelectingLocation(false)
                      }}
                    />
                    {/* Location Suggestions */}
                    {locationSuggestions.length > 0 && (
                      <div className="checkout-location-suggestions" style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        background: 'white',
                        border: '1px solid var(--border)',
                        borderRadius: 'var(--radius-sm)',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        maxHeight: '250px',
                        overflow: 'auto',
                        zIndex: 1000,
                        marginTop: '0.25rem'
                      }}>
                        {locationSuggestions.map((suggestion, index) => (
                          <div
                            key={index}
                            onMouseDown={() => {
                              setIsSelectingLocation(true)
                              setSelectedLocation(suggestion.display_name)
                              setLocationSearch(suggestion.display_name)
                              setLocationSuggestions([])
                            }}
                            style={{
                              padding: '0.75rem 1rem',
                              cursor: 'pointer',
                              borderBottom: index < locationSuggestions.length - 1 ? '1px solid var(--border)' : 'none',
                              transition: 'background 0.2s'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.background = 'var(--bg)'}
                            onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                          >
                            <div style={{ fontSize: '0.9rem', fontWeight: 500, color: 'var(--text)', marginBottom: '0.25rem' }}>
                              {suggestion.display_name}
                            </div>
                            <div style={{ fontSize: '0.75rem', color: 'var(--muted)' }}>
                              {suggestion.address?.road && `${suggestion.address.road}, `}
                              {suggestion.address?.suburb && `${suggestion.address.suburb}, `}
                              {suggestion.address?.city && suggestion.address.city}
                              {suggestion.address?.county && `, ${suggestion.address.county}`}
                              {suggestion.address?.country && `, ${suggestion.address.country}`}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    {locationSearch.length >= 2 && locationSuggestions.length === 0 && !isSearchingLocation && !selectedLocation && (
                      <div className="checkout-location-suggestions" style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        background: 'white',
                        border: '1px solid var(--border)',
                        borderRadius: 'var(--radius-sm)',
                        padding: '1rem',
                        marginTop: '0.25rem',
                        textAlign: 'center',
                        color: 'var(--muted)',
                        fontSize: '0.875rem',
                        zIndex: 1000
                      }}>
                        No locations found. Try adding more details (e.g., "Murua Drive, Nairobi")
                      </div>
                    )}
                    {isSearchingLocation && (
                      <div className="checkout-location-suggestions" style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        background: 'white',
                        border: '1px solid var(--border)',
                        borderRadius: 'var(--radius-sm)',
                        padding: '1rem',
                        marginTop: '0.25rem',
                        textAlign: 'center',
                        color: 'var(--muted)',
                        fontSize: '0.875rem',
                        zIndex: 1000
                      }}>
                        Searching locations...
                      </div>
                    )}
                  </div>
                  <div>
                    <label style={{
                      display: 'block',
                      fontSize: '0.875rem',
                      fontWeight: 600,
                      color: 'var(--text)',
                      marginBottom: '0.5rem'
                    }}>
                  Phone Number *
                </label>
                    <input
                      type="tel"
                      placeholder="e.g. +254 7XX XXX XXX"
                      style={{
                        width: '100%',
                        padding: '0.875rem',
                        border: '2px solid var(--border)',
                        borderRadius: 'var(--radius-sm)',
                        fontSize: '1rem',
                        outline: 'none',
                        transition: 'border-color 0.2s'
                      }}
                      onFocus={(e) => e.currentTarget.style.borderColor = 'var(--red)'}
                      onBlur={(e) => e.currentTarget.style.borderColor = 'var(--border)'}
                    />
                  </div>
                  <div>
                    <label style={{
                      display: 'block',
                      fontSize: '0.875rem',
                      fontWeight: 600,
                      color: 'var(--text)',
                      marginBottom: '0.5rem'
                    }}>
                  Special Instructions (Optional)
                </label>
                    <textarea
                      placeholder="Any special instructions for delivery..."
                      rows={3}
                      style={{
                        width: '100%',
                        padding: '0.875rem',
                        border: '2px solid var(--border)',
                        borderRadius: 'var(--radius-sm)',
                        fontSize: '1rem',
                        outline: 'none',
                        resize: 'vertical',
                        transition: 'border-color 0.2s'
                      }}
                      onFocus={(e) => e.currentTarget.style.borderColor = 'var(--red)'}
                      onBlur={(e) => e.currentTarget.style.borderColor = 'var(--border)'}
                    />
                  </div>
                </div>
              </div>

              {/* Place Order Button */}
              <button
                onClick={async () => {
                  if (!selectedLocation && !locationSearch) {
                    alert('Please enter or select a delivery location')
                    return
                  }
                  const deliveryLocation = selectedLocation || locationSearch
                  
                  // Place order with multi-vendor support
                  try {
                    if (!session) {
                      alert('Please sign in to place an order')
                      return
                    }

                    // Group cart items by vendor
                    const vendorGroups: Record<string, typeof cartItems> = {}
                    cartItems.forEach(item => {
                      const vendorId = item.part!.vendorId || 'default-vendor'
                      if (!vendorGroups[vendorId]) {
                        vendorGroups[vendorId] = []
                      }
                      vendorGroups[vendorId].push(item)
                    })

                    // Store vendor count for success modal
                    setVendorCount(Object.keys(vendorGroups).length)

                    // Generate order reference
                    const reference = `PO${Date.now().toString().slice(-6)}`

                    // Create master order
                    const { data: order, error: orderError } = await supabase
                      .from('part_orders')
                      .insert({
                        reference,
                        customer_id: session.user.id,
                        status: 'pending',
                        delivery_method: 'delivery',
                        delivery_address: deliveryLocation,
                        subtotal: cartTotal,
                        delivery_fee: 0, // Could calculate based on location
                        total_amount: cartTotal,
                        payment_status: 'pending'
                      })
                      .select()
                      .single()

                    if (orderError) throw orderError

                    // Create order items and vendor assignments
                    for (const [vendorId, items] of Object.entries(vendorGroups)) {
                      const vendorSubtotal = items.reduce((sum, item) => sum + (item.part!.price * item.quantity), 0)

                      // Create order items
                      for (const item of items) {
                        await supabase.from('part_order_items').insert({
                          order_id: order.id,
                          part_id: item.part!.id.toString(), // Using string ID for simulation
                          part_name: item.part!.name, // Store part name for display
                          vendor_id: vendorId,
                          quantity: item.quantity,
                          unit_price: item.part!.price,
                          total_price: item.part!.price * item.quantity,
                          item_status: 'pending'
                        })
                      }

                      // Create vendor assignment
                      await supabase.from('part_order_vendor_assignments').insert({
                        order_id: order.id,
                        vendor_id: vendorId,
                        status: 'pending',
                        subtotal: vendorSubtotal,
                        delivery_fee: 0,
                        total_amount: vendorSubtotal
                      })

                      // Notify vendor (skip for simulated vendor IDs)
                      if (!vendorId.startsWith('vendor-')) {
                        await supabase.from('notifications').insert({
                          user_id: vendorId,
                          title: 'New Order Received',
                          body: `Order ${reference} - ${items.length} item(s) to deliver`,
                          type: 'parts'
                        })
                      }
                    }

                    // Notify customer
                    await supabase.from('notifications').insert({
                      user_id: session.user.id,
                      title: 'Order Placed Successfully',
                      body: `Your order ${reference} has been placed. Vendors will confirm shortly.`,
                      type: 'parts'
                    })

                    setShowCheckout(false)
                    setCart({})
                    setSelectedLocation('')
                    setLocationSearch('')
                    setOrderReference(reference)
                    setShowOrderSuccess(true)
                    
                    // Log order placement
                    logActivity('order_placed', '/parts-marketplace', { reference, amount: cartTotal, vendor_count: vendorCount })
                  } catch (error) {
                    console.error('Order placement error:', error)
                    alert('Failed to place order. Please try again.')
                  }
                }}
                style={{
                  width: '100%',
                  padding: '1rem',
                  background: 'var(--red)',
                  border: 'none',
                  borderRadius: 'var(--radius-sm)',
                  cursor: 'pointer',
                  fontWeight: 700,
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.75rem',
                  fontSize: '1.1rem',
                  boxShadow: '0 4px 12px rgba(192, 57, 43, 0.3)',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)'
                  e.currentTarget.style.boxShadow = '0 6px 16px rgba(192, 57, 43, 0.4)'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)'
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(192, 57, 43, 0.3)'
                }}
              >
                <CheckCircle size={20} />
                Place Order · KES {cartTotal.toLocaleString()}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Order Success Modal */}
      {showOrderSuccess && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            backdropFilter: 'blur(4px)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '1rem',
            animation: 'fadeIn 0.2s ease-out'
          }}
          onClick={() => setShowOrderSuccess(false)}
        >
          <div
            style={{
              background: 'white',
              borderRadius: '16px',
              padding: '2.5rem',
              maxWidth: '480px',
              width: '100%',
              textAlign: 'center',
              animation: 'slideInUp 0.3s ease-out',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1.5rem'
            }}>
              <CheckCircle size={40} color="white" />
            </div>
            
            <h2 style={{
              fontSize: '1.75rem',
              fontWeight: 800,
              color: '#1f2937',
              marginBottom: '0.5rem'
            }}>
              Order Placed Successfully!
            </h2>
            
            <p style={{
              fontSize: '1rem',
              color: '#6b7280',
              marginBottom: '1.5rem',
              lineHeight: 1.6
            }}>
              Your order <span style={{ fontWeight: 700, color: '#dc2626' }}>{orderReference}</span> has been placed and is being processed by our vendors.
            </p>

            <div style={{
              background: '#f9fafb',
              borderRadius: '12px',
              padding: '1.25rem',
              marginBottom: '1.5rem',
              border: '1px solid #e5e7eb'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                marginBottom: '0.75rem'
              }}>
                <Package size={20} style={{ color: '#dc2626' }} />
                <span style={{ fontSize: '0.9rem', color: '#374151', fontWeight: 600 }}>
                  {vendorCount} Vendor(s) processing your order
                </span>
              </div>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem'
              }}>
                <Clock size={20} style={{ color: '#dc2626' }} />
                <span style={{ fontSize: '0.9rem', color: '#374151' }}>
                  You'll be notified when vendors confirm
                </span>
              </div>
            </div>

            <button
              onClick={() => {
                setShowOrderSuccess(false)
                navigate('/customer/orders')
              }}
              style={{
                width: '100%',
                padding: '1rem',
                background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                border: 'none',
                borderRadius: '12px',
                cursor: 'pointer',
                fontWeight: 700,
                color: 'white',
                fontSize: '1rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem',
                boxShadow: '0 4px 12px rgba(220, 38, 38, 0.3)',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)'
                e.currentTarget.style.boxShadow = '0 6px 16px rgba(220, 38, 38, 0.4)'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)'
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(220, 38, 38, 0.3)'
              }}
            >
              View My Orders
              <ChevronRight size={18} />
            </button>

            <button
              onClick={() => setShowOrderSuccess(false)}
              style={{
                width: '100%',
                padding: '0.875rem',
                background: 'transparent',
                border: 'none',
                borderRadius: '12px',
                cursor: 'pointer',
                fontWeight: 600,
                color: '#6b7280',
                fontSize: '0.95rem',
                marginTop: '0.75rem',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.color = '#374151'}
              onMouseLeave={(e) => e.currentTarget.style.color = '#6b7280'}
            >
              Continue Shopping
            </button>
          </div>
        </div>
      )}

      {/* Wishlist Modal */}
      {showWishlistModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            backdropFilter: 'blur(4px)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '1rem'
          }}
          onClick={() => setShowWishlistModal(false)}
        >
          <div
            style={{
              background: 'white',
              borderRadius: '16px',
              padding: '2rem',
              maxWidth: '600px',
              width: '100%',
              maxHeight: '80vh',
              overflow: 'auto',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h2 style={{ fontSize: '1.5rem', fontWeight: 800, color: '#1f2937' }}>
                My Wishlist
              </h2>
              <button
                onClick={() => setShowWishlistModal(false)}
                style={{
                  background: 'var(--bg)',
                  border: '1px solid var(--border)',
                  borderRadius: '50%',
                  width: '40px',
                  height: '40px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer'
                }}
              >
                <X size={20} />
              </button>
            </div>

            {wishlistItems.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--muted)' }}>
                <Heart size={48} style={{ margin: '0 auto 1rem', opacity: 0.5 }} />
                <p>Your wishlist is empty</p>
                <button
                  onClick={() => setShowWishlistModal(false)}
                  style={{
                    marginTop: '1rem',
                    padding: '0.75rem 1.5rem',
                    background: 'var(--red)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontWeight: 600,
                    cursor: 'pointer'
                  }}
                >
                  Browse Parts
                </button>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {wishlistItems.map((item) => (
                  <div
                    key={item.id}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '1rem',
                      background: 'var(--bg)',
                      borderRadius: '8px',
                      border: '1px solid var(--border)'
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '1rem', fontWeight: 600, color: '#1f2937', marginBottom: '0.25rem' }}>
                        {item.part_name}
                      </div>
                      <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                        KES {item.part_price.toLocaleString()}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button
                        onClick={() => {
                          console.log('Add to cart from wishlist clicked for part_id:', item.part_id)
                          const part = parts.find(p => p.id === item.part_id)
                          console.log('Found part:', part)
                          if (part) {
                            addToCart(part.id)
                          } else {
                            alert('This part is no longer available')
                          }
                        }}
                        style={{
                          padding: '0.5rem 1rem',
                          background: 'var(--red)',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          fontWeight: 600,
                          cursor: 'pointer',
                          fontSize: '0.875rem'
                        }}
                      >
                        Add to Cart
                      </button>
                      <button
                        onClick={() => {
                          console.log('Remove from wishlist clicked for part_id:', item.part_id)
                          toggleWishlist(item.part_id)
                        }}
                        style={{
                          padding: '0.5rem',
                          background: '#fee2e2',
                          border: '1px solid #fecaca',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <Trash2 size={16} color='#dc2626' />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
